// Sistema de caché simple para peticiones API
const cache = new Map();
const pendingRequests = new Map();

const DEFAULT_TTL = 2500 * 1000;

/**
 * Obtiene datos de la caché o ejecuta la función y cachea el resultado
 * @param {string} key - Clave única para identificar la petición
 * @param {Function} fetchFn - Función que retorna una Promise con los datos
 * @param {number} ttl - Tiempo de vida en milisegundos (por defecto 30s)
 * @returns {Promise} - Los datos cacheados o frescos
 */
export const getCachedData = async (key, fetchFn, ttl = DEFAULT_TTL) => {
    const now = Date.now();
    const cached = cache.get(key);

    // Si hay datos en caché y no han expirado, retornarlos
    if (cached && (now - cached.timestamp) < ttl) {
        return cached.data;
    }

    // Si ya hay una petición en curso para esta key, esperar a que termine
    if (pendingRequests.has(key)) {
        return pendingRequests.get(key);
    }

    // Crear nueva petición
    const request = fetchFn().then(data => {
        cache.set(key, { data, timestamp: Date.now() });
        pendingRequests.delete(key);
        return data;
    }).catch(error => {
        pendingRequests.delete(key);
        throw error;
    });

    pendingRequests.set(key, request);
    return request;
};

/**
 * Invalida una entrada específica de la caché
 * @param {string} key - Clave a invalidar
 */
export const invalidateCache = (key) => {
    cache.delete(key);
};

/**
 * Invalida todas las entradas que empiecen con un prefijo
 * @param {string} prefix - Prefijo de las claves a invalidar
 */
export const invalidateCacheByPrefix = (prefix) => {
    for (const key of cache.keys()) {
        if (key.startsWith(prefix)) {
            cache.delete(key);
        }
    }
};

/**
 * Limpia toda la caché
 */
export const clearCache = () => {
    cache.clear();
};

// Claves de caché predefinidas
export const CACHE_KEYS = {
    DASHBOARD_STATS: 'dashboard_stats',
    MIS_SOLICITUDES: 'mis_solicitudes',
    CALENDARIO: 'calendario',
    PROXIMOS_CUMPLEANOS: 'proximos_cumpleanos',
    FESTIVOS: 'festivos',
    PERIODOS_VERANO: 'periodos_verano',
    PERIODOS_VERANO_COLAB: 'periodos_verano_colab',
    CALENDARIOS_LABORALES: 'calendarios_laborales'
};
